'use client';

import { useIsClient } from 'usehooks-ts';
import { useGeoSearchString } from '@/contexts/CookieContext';
import React, { Fragment, useMemo } from 'react';
import { buildGeoPrefixedPath } from '../../main-page/CategoriesShortcuts/GeoAwareCategoryLink.client';
import { getLink } from '@/utils/serverSetCategoryLink';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';

type Breadcrumb = { url: string; name: string };

const normalizePath = (value?: string | null) => (value || '').replace(/^\/+|\/+$/g, '');

function GeoAwareBreadcrumbsInternal({
  breadcrumbs,
  hideLastElem = true,
  initialSearchLink = '',
}: {
  breadcrumbs: Breadcrumb[];
  hideLastElem?: boolean;
  initialSearchLink?: string;
}) {
  const geoSearchString = useGeoSearchString();
  const mounted = useIsClient();

  const normalizedGeo = mounted ? normalizePath(geoSearchString) : '';

  const geoFixedBreadcrumbs = useMemo(() => {
    return breadcrumbs.map((bc) => ({
      name: bc.name,
      resUrl: mounted ? buildGeoPrefixedPath(normalizedGeo, bc.url) : getLink(`/${bc.url}`, initialSearchLink),
    }));
  }, [normalizedGeo, breadcrumbs, mounted, initialSearchLink]);

  const lastIndex = geoFixedBreadcrumbs.length - 1;

  return (
    <Breadcrumb suppressHydrationWarning className='w-full min-w-0'>
      <BreadcrumbList className='flex-wrap gap-1 text-[12px] leading-5 text-text-secondary md:gap-1.5 md:text-[14px] sm:gap-1.5'>
        {geoFixedBreadcrumbs.map(({ resUrl, name }, index) => {
          const isLast = index === lastIndex;
          return (
            <Fragment key={`${resUrl}-${index}`}>
              <BreadcrumbItem className='min-w-0 max-w-full shrink-0 whitespace-normal [overflow-wrap:anywhere] md:max-w-none md:whitespace-nowrap md:[overflow-wrap:normal]'>
                {isLast && hideLastElem ? (
                  <BreadcrumbPage className='font-medium text-text-primary'>{name}</BreadcrumbPage>
                ) : (
                  <BreadcrumbLink
                    href={resUrl}
                    data-test-test={resUrl}
                    className='rounded-[4px] text-text-secondary outline-offset-2 hover:text-text-primary focus-visible:outline-2 focus-visible:outline-main-green'
                  >
                    {name}
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {!isLast && <BreadcrumbSeparator className='shrink-0 text-icon-default [&>svg]:size-3.5' />}
            </Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

export const GeoAwareBreadcrumbs = React.memo(GeoAwareBreadcrumbsInternal);
